import React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Table } from '@factorialco/deprecated-design-system'
import { EstimatedAndWorkedTimeDocument } from 'generated/resources/graphql'
import { sortBy } from 'lodash'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'

import AddCommentsHint from 'modules/project_management/components/hints/myProjects/AddCommentsHint'
import AddWorkedHoursHint from 'modules/project_management/components/hints/myProjects/AddWorkedHoursHint'
import AddProjectModal, {
  AddProjectFormValues,
} from 'modules/project_management/components/modals/AddProjectFlexibleTrackingModal'
import { MyProjectsCommentsSidepanel } from 'modules/project_management/components/myProjects/Sidepanel'
import EmptyTableMessage from 'modules/project_management/components/tables/MyProjectsTable/_Body/EmptyTableMessage'
import {
  WIDTH_TABLE,
  type maxMinutesPerCurrentWeekTypes,
} from 'modules/project_management/lib/constants'
import {
  CustomGlobalTimeRecord,
  getWeeklyImputedMinutesList,
} from 'modules/project_management/lib/getCustomGlobalTimeRecordsForMyProjectsTable'
import type ProjectsStore from 'modules/project_management/store/models/Projects'

import { ICurrentWeekHeaderInfo } from 'modules/project_management/components/tables/MyProjectsTable/_Header'
import Row from './Row'
import Footer from './_Footer'

interface BodyProps {
  currentWeekHeaderInfo: ICurrentWeekHeaderInfo[]
  favouriteProjects: CustomGlobalTimeRecord[]
  flexibleTimeRecords: CustomGlobalTimeRecord[]
  hasEmployeeGlobalTimeRecords: (hasGlobalTimeRecords: boolean) => void
  hasFavouriteProjectsSelected: boolean
  hasLastWeekProjectsSelected: boolean
  lastWeekProjects: CustomGlobalTimeRecord[]
  projectsStore: ProjectsStore
  weekStart: string
  weekEnd: string
  canRegisterFlexibleTracking: boolean
  isAddProjectModalOpen: boolean
  setIsAddProjectModalOpen: (isOpen: boolean) => void
  isCommentsSidepanelOpen: boolean
  setCommentsSidepanelOpen: (isOpen: boolean) => void
}

const Body = ({
  currentWeekHeaderInfo,
  favouriteProjects,
  flexibleTimeRecords,
  hasEmployeeGlobalTimeRecords,
  hasFavouriteProjectsSelected,
  hasLastWeekProjectsSelected,
  lastWeekProjects,
  projectsStore,
  weekStart,
  weekEnd,
  canRegisterFlexibleTracking,
  isAddProjectModalOpen,
  setIsAddProjectModalOpen,
  isCommentsSidepanelOpen,
  setCommentsSidepanelOpen,
}: BodyProps) => {
  const [temporaryRecords, setTemporaryRecords] = React.useState<CustomGlobalTimeRecord[]>([])
  const hasGlobalTimeRecords = flexibleTimeRecords.concat(temporaryRecords).length > 0

  const getNewTemporaryRecords = (projects: CustomGlobalTimeRecord[]) => {
    return temporaryRecords.filter((tr) => {
      return !projects.some(
        (project) =>
          project.projectWorkerId === tr.projectWorkerId && project.subprojectId === tr.subprojectId
      )
    })
  }
  const updateTemporaryRecordsFavouriteIds = (projectsToUpdate: CustomGlobalTimeRecord[]) => {
    return projectsToUpdate.map((tr) => {
      const existInFavourites = favouriteProjects.some(
        (fp) => fp.projectWorkerId === tr.projectWorkerId && fp.subprojectId === tr.subprojectId
      )
      if (!existInFavourites) tr.favouriteProjectId = undefined
    })
  }

  useEffect(() => {
    setTemporaryRecords([])
  }, [weekStart])

  useEffect(() => {
    if (temporaryRecords.length !== 0) {
      const newTemporaryProjects = getNewTemporaryRecords(favouriteProjects)
      updateTemporaryRecordsFavouriteIds(newTemporaryProjects)
      setTemporaryRecords(newTemporaryProjects)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favouriteProjects.length, hasFavouriteProjectsSelected])

  useEffect(() => {
    if (temporaryRecords.length !== 0 && hasLastWeekProjectsSelected) {
      const newTemporaryProjects = getNewTemporaryRecords(lastWeekProjects)
      setTemporaryRecords(newTemporaryProjects)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLastWeekProjectsSelected])

  const { employeeId } = useParams()
  const employeeIdNumber = Number(employeeId)

  const { data: workedAndEstimated } = useQuery(EstimatedAndWorkedTimeDocument, {
    variables: {
      startOn: weekStart,
      endOn: weekEnd,
      employeeId: employeeIdNumber,
    },
  })

  const workedTimeList = workedAndEstimated?.attendance.workedTimesConnection.nodes || []
  const estimatedTimeList =
    workedAndEstimated?.attendance?.employee?.attendanceEstimatedTimesConnection.nodes || []

  const getMaxMinutesPerDayForTheCurrentWeek = (): {
    minutes: number
    type: maxMinutesPerCurrentWeekTypes
  }[] => {
    const maxMinutesPerDay = Array.from({ length: 7 }, (_, i) => {
      const currentDate = moment(weekStart).add(i, 'days').format('YYYY-MM-DD')
      const workedTimeForCurrentDateIndex = workedTimeList.findIndex(
        (item) => item.date === currentDate
      )
      const estimatedTimeForCurrentDateIndex = estimatedTimeList.findIndex(
        (item) => item.date === currentDate
      )

      const workedMinutes =
        workedTimeList[workedTimeForCurrentDateIndex]?.effectiveWorkedMinutes || 0
      const estimatedMinutes = estimatedTimeList[estimatedTimeForCurrentDateIndex]?.minutes || 0

      if (workedMinutes === 0 && estimatedMinutes === 0)
        return { minutes: 0, type: 'estimated' as const }
      if (workedMinutes === 0) return { minutes: estimatedMinutes, type: 'estimated' as const }

      return { minutes: workedMinutes, type: 'worked' as const }
    })

    return maxMinutesPerDay
  }

  const onSubmit = (data: AddProjectFormValues) => {
    const existingProject = flexibleTimeRecords
      .concat(temporaryRecords)
      .find(
        (p) => p.projectWorkerId === data.projectWorkerId && p.subprojectId === data.subprojectId
      )
    const existingFavouriteProject = favouriteProjects.find(
      (fp) => fp.projectWorkerId === data.projectWorkerId && fp.subprojectId === data.subprojectId
    )

    if (!existingProject) {
      const newRecord = {
        favouriteProjectId: existingFavouriteProject?.favouriteProjectId,
        projectId: data.projectId,
        projectName: data.projectName,
        projectStatus: data.projectStatus,
        projectAssigned: data.projectAssigned,
        projectWorkerId: data.projectWorkerId,
        subprojectName: data.subprojectName,
        subprojectId: data.subprojectId,
        imputedMinutesList: getWeeklyImputedMinutesList(weekStart, 0, null, null, true),
      }

      setTemporaryRecords(temporaryRecords.concat(newRecord))
    }

    onClose()
  }
  const onClose = () => {
    setIsAddProjectModalOpen(false)
  }

  const onCloseCommentsSidepanel = () => {
    setCommentsSidepanelOpen(false)
  }

  const getTotalDayInputedMinutes = () => {
    const arraySum = flexibleTimeRecords
      .concat(temporaryRecords)
      .map((item) => item?.imputedMinutesList)
      .map((item) =>
        item.map((element) => element.imputedMinutes + (element.openClockInMinutes || 0))
      )
      .reduce((firstInputedMinutesList, otherInputedMinutesList) =>
        firstInputedMinutesList.map(
          (firstInputedMinutes, index) =>
            firstInputedMinutes + (otherInputedMinutesList[index] || 0)
        )
      )
    return arraySum
  }

  const emptyTable = () => {
    return (
      <Table.Body>
        <Table.Row>
          <Table.Cell colSpan={WIDTH_TABLE} paddingY="s48">
            <EmptyTableMessage
              addProject={() => setIsAddProjectModalOpen(true)}
              canRegisterFlexibleTracking={canRegisterFlexibleTracking}
            />
          </Table.Cell>
        </Table.Row>
        <Footer totalDayInputedMinutes={[0, 0, 0, 0, 0, 0, 0]} />
      </Table.Body>
    )
  }

  const sortedRecords = sortBy(flexibleTimeRecords.concat(temporaryRecords), [
    (tr) => !tr.favouriteProjectId,
    'projectName',
    (tr) => tr.subprojectName || '',
  ])

  hasEmployeeGlobalTimeRecords(hasGlobalTimeRecords)

  return (
    <>
      {hasGlobalTimeRecords ? (
        <Table.Body>
          {sortedRecords.map((customGlobalTimeRecord, index) => (
            <>
              <Row
                key={index}
                currentWeekHeaderInfo={currentWeekHeaderInfo}
                customGlobalTimeRecord={customGlobalTimeRecord}
                temporaryRecords={temporaryRecords}
                canRegisterFlexibleTracking={canRegisterFlexibleTracking}
                projectsStore={projectsStore}
              />
              {index === 0 && (
                <Box paddingLeft="s300">
                  <AddWorkedHoursHint stepNumber={2} position="bottom right" />
                  <AddCommentsHint stepNumber={3} />
                </Box>
              )}
            </>
          ))}
          <Footer
            maxMinutesPerCurrentWeek={getMaxMinutesPerDayForTheCurrentWeek()}
            totalDayInputedMinutes={getTotalDayInputedMinutes()}
          />
        </Table.Body>
      ) : (
        emptyTable()
      )}
      {isAddProjectModalOpen && (
        <AddProjectModal
          projectsStore={projectsStore}
          onClose={onClose}
          onSubmit={onSubmit}
          employeeId={employeeIdNumber}
        />
      )}
      {isCommentsSidepanelOpen && (
        <MyProjectsCommentsSidepanel
          onClose={onCloseCommentsSidepanel}
          open={isCommentsSidepanelOpen}
          sortedRecords={sortedRecords}
          projectsStore={projectsStore}
          weekStart={weekStart}
          weekEnd={weekEnd}
        />
      )}
    </>
  )
}

export default Body
